import { useTranslation } from "react-i18next";
import { Box } from "@mui/system";
import RedirectingComponent from "src/chamber/components/RedirectingComponent";

type filters = {
  legalTypes: { d08_id: number; d08_descr: string }[];
  appTypes: { label: string; value: number }[];
  chamberUsers: { label: string; value: number }[];
  isOrgAdmin: boolean;
  responsibleNow: {
    label: string;
    value: number;
  }[];
  statuses: {
    label: string;
    value: number;
  }[];
};
type badgeCountsApplication =
  | {
      useForCertificate: false;
      activeOwnOnlyApplicationsCount: number | null;
      activeOwnApplications: number | null;
      activeChamberApplicationsPaidCount: number | null;
      activeChamberApplicationsUnpaidCount: number | null;
      unsignedOwnCertificateApplicationsCount: number | null;
    }
  | {
      useForCertificate: true;
      activeOwnCertificateApplicationsCount: number | null;
      activeChamberCertificateApplicationsPaidCount: number | null;
      activeChamberCertificateApplicationsUnpaidCount: number | null;
      unsignedOwnCertificateApplicationsCount: number | null;
    };
type ApplicationRedirectorProps = filters &
  badgeCountsApplication & { countsLoading?: boolean };

function ApplicationRedirector(props: ApplicationRedirectorProps) {
  const { t } = useTranslation();
  const allFilters = [
    {
      filterName: "legalType",
      filterLabel: t("Ανά Νομική Μορφή"),
      filterOptions: props?.legalTypes?.map((option) => ({
        label: option?.d08_descr,
        value: option?.d08_id,
      })),
    },
    {
      filterName: "appType",
      filterLabel: t("Ανά τύπο αίτησης"),
      filterOptions: props.appTypes,
    },
    {
      filterName: "chamberUser",
      filterLabel: t("Ανά Χρήστη"),
      filterOptions: props.chamberUsers,
    },
    {
      filterName: "returnedFrom",
      filterLabel: t("Επιστροφή αίτησης από:"),
      filterOptions: [
        {
          label: t("ΠΕΡΙΦΕΡΕΙΑ"),
          value: "REGION",
        },
        {
          label: t("ΕΠΙΧΕΙΡΗΣΗ"),
          value: "COMPANY",
        },
      ],
    },
    {
      filterName: "responsibleNow",
      filterLabel: t("Ανά φορέα που επιλαμβάνεται της υπόθεσης"),
      filterOptions: props.responsibleNow,
    },
    {
      filterName: "status",
      filterLabel: t("Ανά κατάσταση αίτησης"),
      filterOptions: props.statuses,
    },
  ];
  const redirectors = props.useForCertificate
    ? [
        {
          linkText: t("Πιστοποιητικά που απαιτούν υπογραφή από εμένα"),
          linkTo: "/chamber/unsignedOwnCertificateApplications",
          badgeCount: props.unsignedOwnCertificateApplicationsCount,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) =>
            ["legalType"].includes(filter?.filterName),
          ),
        },
        {
          linkText: props.isOrgAdmin
            ? t("Αιτήσεις σε εξέλιξη (δικές μου ή γενικά στην Υ. Γ.Ε.ΜΗ.)")
            : t("Αιτήσεις σε εξέλιξη (δικές μου)"),
          linkTo: "/chamber/activeOwnCertificateApplications",
          badgeCount: props.activeOwnCertificateApplicationsCount,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) =>
            ["legalType", "chamberUser"].includes(filter?.filterName),
          ),
        },
        {
          linkText: t(
            "Αιτήσεις που δεν έχουν ανατεθεί ακόμη - Χωρίς εκκρεμότητα κάποιας πληρωμής",
          ),
          linkTo: "/chamber/activeChamberCertificateApplicationsPaid",
          badgeCount: props.activeChamberCertificateApplicationsPaidCount,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) =>
            ["legalType", "chamberUser"].includes(filter?.filterName),
          ),
        },
        {
          linkText: t(
            "Αιτήσεις που δεν έχουν ανατεθεί ακόμη - Με οποιαδήποτε εκκρεμότητα κάποιας πληρωμής",
          ),
          linkTo: "/chamber/activeChamberCertificateApplicationsUnpaid",
          badgeCount: props.activeChamberCertificateApplicationsUnpaidCount,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) =>
            ["legalType", "chamberUser"].includes(filter?.filterName),
          ),
        },
        {
          linkText: t("Αρχείο Εκπληρωμένων Υποθέσεων"),
          linkTo: "/chamber/pastOwnCertificateApplications",
          badgeCount: null,
          filters: allFilters.filter((filter) =>
            ["legalType", "chamberUser"].includes(filter?.filterName),
          ),
        },
        ...(props.isOrgAdmin
          ? [
              {
                linkText: t("Ανατεθειμένες σε διεγραμμένο χρήστη"),
                linkTo: "/chamber/inactiveCertificateApplications",
                badgeCount: null,
                filters: [],
              },
            ]
          : []),
      ]
    : [
        {
          linkText: t("Αιτήσεις που απαιτούν δική μου ενέργεια"),
          linkTo: "/chamber/activeOwnOnlyApplications",
          badgeCount: props.activeOwnOnlyApplicationsCount,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) =>
            ["legalType", "appType", "chamberUser", "returnedFrom"].includes(
              filter?.filterName,
            ),
          ),
        },
        {
          linkText: !props.isOrgAdmin
            ? t("Αιτήσεις σε εξέλιξη (δικές μου)")
            : t("Αιτήσεις σε εξέλιξη (δικές μου ή γενικά στην Υ. Γ.Ε.ΜΗ.)"),
          linkTo: "/chamber/activeOwnApplications",
          badgeCount: props.activeOwnApplications,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) =>
            ["legalType", "appType", "chamberUser", "responsibleNow"].includes(
              filter?.filterName,
            ),
          ),
        },
        {
          linkText: t(
            "Αιτήσεις που δεν έχουν ανατεθεί ακόμη - Χωρίς εκκρεμότητα κάποιας πληρωμής",
          ),
          linkTo: "/chamber/activeChamberApplicationsPaid",
          badgeCount: props.activeChamberApplicationsPaidCount,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) => {
            return ["legalType", "appType"].includes(filter?.filterName);
          }),
        },
        {
          linkText: t("Αιτήσεις με οποιαδήποτε εκκρεμότητα πληρωμής	"),
          linkTo: "/chamber/activeChamberApplicationsUnpaid",
          badgeCount: props.activeChamberApplicationsUnpaidCount,
          badgeLoading: props.countsLoading,
          filters: allFilters.filter((filter) => {
            return ["legalType", "appType"].includes(filter?.filterName);
          }),
        },
        {
          linkText: t("Αρχείο Εκπληρωμένων Υποθέσεων"),
          linkTo: "/chamber/pastOwnApplications",
          badgeCount: null,
          filters: allFilters.filter((filter) => {
            return ["legalType", "appType", "chamberUser", "status"].includes(
              filter?.filterName,
            );
          }),
        },
        ...(props.isOrgAdmin
          ? [
              {
                linkText: t("Ανατεθειμένες σε διεγραμμένο χρήστη"),
                linkTo: "/chamber/inactiveApplications",
                badgeCount: null,
                filters: [],
              },
            ]
          : []),
      ];
  return (
    <Box
      sx={{
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {redirectors.map((redirector) => (
        <RedirectingComponent {...redirector} />
      ))}
    </Box>
  );
}

export default ApplicationRedirector;
